import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useQuery } from "react-query";
import Pagination from "react-js-pagination";
import moment from "moment";
import AdminLayout from "../../../components/layouts/adminLayout";
import AdminPageContainer from "../../../components/AdminPageContainer";
import LicenseFinderLeadsService from "../../../services/API/LicenseFinderLeads";
import useAuth from "../../../hooks/useAuth";
import { ROLES } from "../../../constant/roles";

const POSTS_PER_PAGE = 20;

// Sortable columns; keys match the sort names the API accepts
const COLUMNS = [
  { key: "full_name", label: "Full Name" },
  { key: "mobile", label: "Mobile" },
  { key: "business_type", label: "Business Type" },
  { key: "state", label: "State" },
  { key: "created_at", label: "Created Date" },
];

export default function LicenseFinderLeads() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [displaySearchField, setDisplaySearchField] = useState(false);
  const [sortColumn, setSortColumn] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");

  // Same page is served to Alliance Manager and Customer Support
  const homePath =
    Number(auth?.roleId) === ROLES.customerSupport
      ? "/customer-support/dashboard"
      : "/alliance-manager/dashboard";

  const { data, isLoading, isFetching } = useQuery(
    [
      "api/list-license-finder-leads",
      { q, page, limit: POSTS_PER_PAGE, sortColumn, sortOrder },
    ],
    LicenseFinderLeadsService.getLicenseFinderLeads,
    { refetchOnWindowFocus: false, keepPreviousData: true }
  );

  const leads = data?.data?.data ?? [];
  const totalCount = data?.data?.count ?? 0;

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [page]);

  // Clicking the active column flips the order; a new column starts ascending
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const sortClass = (column) =>
    sortColumn === column
      ? sortOrder === "asc"
        ? "sorting_asc"
        : "sorting_desc"
      : "sorting";

  return (
    <AdminLayout test="active">
      <AdminPageContainer>
        <div id="main" role="main">
          <div id="content">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a
                  className="text-primary cursor-pointer"
                  onClick={() => navigate(homePath)}
                  onKeyDown={() => navigate(homePath)}
                >
                  Home
                </a>
              </li>
              <li className="breadcrumb-item active">License Finder Leads</li>
            </ol>

            <div className="row">
              <div className="card card-sm card-shadow mb-3">
                <div className="card-body">
                  <h2 className="fw-bold mb-0">License Finder Leads</h2>

                  <div className="d-flex justify-content-end align-items-center gap-2 pb-3">
                    {!!q && (
                      <button
                        type="button"
                        className="button_new pull-right mt-1 px-3"
                        onClick={() => {
                          setQ("");
                          setPage(1);
                          setDisplaySearchField(false);
                        }}
                      >
                        Clear Filters
                      </button>
                    )}
                    {displaySearchField ? (
                      <label className="input m-0 mt-1">
                        <input
                          type="text"
                          className="button_new"
                          value={q}
                          onChange={(e) => {
                            setPage(1);
                            setQ(e.target.value);
                          }}
                          placeholder="Search"
                        />
                      </label>
                    ) : (
                      <a
                        className="button_new"
                        onClick={() => setDisplaySearchField(true)}
                        onKeyDown={() => setDisplaySearchField(true)}
                      >
                        <i
                          className="fa fa-fa fa-search requests-icons"
                          title="Search"
                          aria-hidden="true"
                        ></i>
                      </a>
                    )}
                  </div>

                  <div className="table-responsive">
                    <table className="table table-bordered table-hover dataTable no-footer">
                      <thead>
                        <tr role="row" className="fw-bold">
                          <th className="text-center" width="60px">
                            S. No.
                          </th>
                          {COLUMNS.map(({ key, label }) => (
                            <th
                              key={key}
                              onClick={() => handleSort(key)}
                              className={`text-center align-top py-2 td-sr ${sortClass(key)}`}
                              aria-sort={
                                sortColumn === key
                                  ? sortOrder === "asc"
                                    ? "ascending"
                                    : "descending"
                                  : "none"
                              }
                            >
                              {label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {leads.length !== 0 ? (
                          leads.map((lead, index) => (
                            <tr key={lead?.id}>
                              <td className="text-start align-middle">
                                {POSTS_PER_PAGE * (page - 1) + index + 1}
                              </td>
                              <td className="text-start align-middle">
                                {lead?.full_name}
                              </td>
                              <td className="text-start align-middle">
                                {lead?.mobile}
                              </td>
                              <td className="text-start align-middle">
                                {lead?.business_type || "-"}
                              </td>
                              <td className="text-start align-middle">
                                {lead?.state || "-"}
                              </td>
                              <td className="text-start align-middle">
                                {moment(lead?.created_at).isValid()
                                  ? moment(lead?.created_at).format(
                                      "DD-MM-YY hh:mm A"
                                    )
                                  : "-"}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={COLUMNS.length + 1}
                              className="p-2 text-center"
                            >
                              {isLoading || isFetching
                                ? "Loading..."
                                : "No data found"}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    {totalCount > POSTS_PER_PAGE && (
                      <Pagination
                        activePage={page}
                        itemsCountPerPage={POSTS_PER_PAGE}
                        totalItemsCount={totalCount}
                        pageRangeDisplayed={5}
                        onChange={setPage}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AdminPageContainer>
    </AdminLayout>
  );
}
